'use client';

import { jsPDF } from 'jspdf';

export type PdfJsViewport = {
  width: number;
  height: number;
};

export type PdfJsTextItem = {
  str: string;
  transform: number[];
  width: number;
  height: number;
  hasEOL?: boolean;
};

export type PdfJsTextContent = {
  items: PdfJsTextItem[];
};

export type PdfJsPage = {
  getViewport: (params: { scale: number }) => PdfJsViewport;
  render: (params: {
    canvasContext: CanvasRenderingContext2D;
    canvas: HTMLCanvasElement;
    viewport: PdfJsViewport;
  }) => { promise: Promise<void> };
  getTextContent?: () => Promise<PdfJsTextContent>;
  cleanup?: () => void;
};

export type PdfJsDocument = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfJsPage>;
  cleanup?: () => void;
  destroy?: () => Promise<void> | void;
};

export type PdfJsModule = {
  GlobalWorkerOptions: {
    workerSrc: string;
  };
  getDocument: (params: {
    data: Uint8Array;
    disableWorker: boolean;
    useSystemFonts?: boolean;
    isEvalSupported?: boolean;
    password?: string;
  }) => {
    promise: Promise<PdfJsDocument>;
  };
};

export type PdfProgressState = {
  current: number;
  total: number;
  label: string;
};

export type PdfCropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type RenderedPdfRebuildResult = {
  blob: Blob;
  recoveredPages: number[];
  droppedPages: number[];
};

const DEFAULT_MAX_RENDER_EDGE = 2200;
const DEFAULT_MAX_CANVAS_PIXELS = 6_500_000;

let pdfJsModulePromise: Promise<PdfJsModule> | null = null;

export function loadPdfJsModule() {
  if (!pdfJsModulePromise) {
    pdfJsModulePromise =
      import('pdfjs-dist/legacy/build/pdf.mjs') as unknown as Promise<PdfJsModule>;
  }

  return pdfJsModulePromise.then((module) => {
    if (!module.GlobalWorkerOptions.workerSrc) {
      module.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
    }

    return module;
  });
}

export function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

export function formatExportTimestamp(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
}

export function createCleanorExportFileName(
  prefix: string,
  parts: Array<number | string>,
  extension: string,
) {
  const slug = [prefix, ...parts]
    .map((part) =>
      String(part)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, ''),
    )
    .filter(Boolean)
    .join('-');

  return `cleanor.app-${slug}-${formatExportTimestamp(new Date())}.${extension}`;
}

export function triggerBrowserDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

export function isPdfFile(file: File) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

export function isImageFile(file: File) {
  return file.type.startsWith('image/');
}

export async function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: 'image/jpeg' | 'image/png',
  quality?: number,
) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error('The browser could not encode this PDF page.'));
      },
      mimeType,
      quality,
    );
  });
}

export function getRenderScale(
  viewport: PdfJsViewport,
  preferredScale: number,
  maxRenderEdge = DEFAULT_MAX_RENDER_EDGE,
  maxCanvasPixels = DEFAULT_MAX_CANVAS_PIXELS,
) {
  const basePixels = viewport.width * viewport.height;
  const edgeScale = maxRenderEdge / Math.max(viewport.width, viewport.height);
  const pixelScale = Math.sqrt(maxCanvasPixels / Math.max(1, basePixels));

  return Math.max(1, Math.min(preferredScale, edgeScale, pixelScale));
}

export async function renderPdfPageToCanvas(
  page: PdfJsPage,
  scale: number,
  background = '#ffffff',
) {
  const viewport = page.getViewport({ scale });
  const canvas = window.document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(viewport.width));
  canvas.height = Math.max(1, Math.round(viewport.height));

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Your browser could not render this PDF page.');
  }

  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: context, canvas, viewport }).promise;

  return { canvas, viewport };
}

export async function createPdfPreviewUrl(pdfDocument: PdfJsDocument) {
  const page = await pdfDocument.getPage(1);

  try {
    const baseViewport = page.getViewport({ scale: 1 });
    const previewScale = Math.max(
      0.7,
      Math.min(1.05, 280 / Math.max(baseViewport.width, baseViewport.height)),
    );
    const { canvas } = await renderPdfPageToCanvas(page, previewScale);
    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.82);
    canvas.width = 0;
    canvas.height = 0;
    return URL.createObjectURL(blob);
  } finally {
    page.cleanup?.();
  }
}

export async function createPdfPagePreviewUrl(
  page: PdfJsPage,
  preferredEdge = 220,
  background = '#ffffff',
) {
  const baseViewport = page.getViewport({ scale: 1 });
  const previewScale = Math.max(
    0.45,
    Math.min(1.1, preferredEdge / Math.max(baseViewport.width, baseViewport.height)),
  );
  const { canvas } = await renderPdfPageToCanvas(page, previewScale, background);
  const blob = await canvasToBlob(canvas, 'image/jpeg', 0.82);
  canvas.width = 0;
  canvas.height = 0;
  return URL.createObjectURL(blob);
}

export async function openPdfDocument(bytes: Uint8Array, password?: string) {
  const pdfjs = await loadPdfJsModule();
  const loadingTask = pdfjs.getDocument({
    data: bytes,
    disableWorker: true,
    useSystemFonts: true,
    isEvalSupported: false,
    password,
  });

  return loadingTask.promise;
}

export function buildPdfText(items: PdfJsTextItem[]) {
  const chunks: string[] = [];

  for (const item of items) {
    const value = item.str ?? '';
    if (!value.trim()) {
      if (chunks.length && !chunks[chunks.length - 1].endsWith(' ')) {
        chunks[chunks.length - 1] += ' ';
      }
      continue;
    }

    chunks.push(value);

    if (item.hasEOL) {
      chunks.push('\n');
    } else if (!value.endsWith('-')) {
      chunks.push(' ');
    }
  }

  return chunks
    .join('')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function parsePageRanges(input: string, totalPages: number) {
  const values = input
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  if (!values.length) {
    throw new Error('Enter at least one page number or range.');
  }

  const pages = new Set<number>();

  for (const value of values) {
    if (/^\d+$/.test(value)) {
      const pageNumber = Number(value);
      if (pageNumber < 1 || pageNumber > totalPages) {
        throw new Error(`Page ${pageNumber} is outside this PDF.`);
      }
      pages.add(pageNumber);
      continue;
    }

    const match = value.match(/^(\d+)\s*-\s*(\d+)$/);
    if (!match) {
      throw new Error(`"${value}" is not a valid page range.`);
    }

    const start = Number(match[1]);
    const end = Number(match[2]);
    if (start < 1 || end > totalPages || start > end) {
      throw new Error(`"${value}" is outside this PDF.`);
    }

    for (let pageNumber = start; pageNumber <= end; pageNumber += 1) {
      pages.add(pageNumber);
    }
  }

  return Array.from(pages).sort((left, right) => left - right);
}

export async function extractPdfPageTexts(
  pdfDocument: PdfJsDocument,
  pageNumbers: number[],
  onProgress?: (progress: PdfProgressState) => void,
) {
  const pages: Array<{ pageNumber: number; text: string; items: PdfJsTextItem[] }> = [];

  for (let index = 0; index < pageNumbers.length; index += 1) {
    const pageNumber = pageNumbers[index];
    onProgress?.({
      current: index + 1,
      total: pageNumbers.length,
      label: `Reading text from page ${pageNumber}…`,
    });

    const page = await pdfDocument.getPage(pageNumber);

    try {
      const textContent = await page.getTextContent?.();
      const items = textContent?.items ?? [];
      pages.push({
        pageNumber,
        items,
        text: buildPdfText(items),
      });
    } finally {
      page.cleanup?.();
    }
  }

  return pages;
}

export async function rebuildPdfFromRenderedPages(params: {
  pdfDocument: PdfJsDocument;
  pageNumbers?: number[];
  jpegQuality?: number;
  onProgress?: (progress: PdfProgressState) => void;
  encryption?: {
    userPassword: string;
    ownerPassword?: string;
    permissions?: Array<'print' | 'copy'>;
  };
}) {
  const {
    pdfDocument,
    pageNumbers = Array.from({ length: pdfDocument.numPages }, (_, index) => index + 1),
    jpegQuality = 0.82,
    onProgress,
    encryption,
  } = params;

  let exportDoc: jsPDF | null = null;
  const recoveredPages: number[] = [];
  const droppedPages: number[] = [];

  for (let index = 0; index < pageNumbers.length; index += 1) {
    const pageNumber = pageNumbers[index];
    onProgress?.({
      current: index + 1,
      total: pageNumbers.length,
      label: `Rendering page ${pageNumber} of ${pageNumbers.length}…`,
    });

    let page: PdfJsPage | null = null;

    try {
      page = await pdfDocument.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const renderScale = getRenderScale(baseViewport, 1.8);
      const { canvas, viewport } = await renderPdfPageToCanvas(page, renderScale);
      const blob = await canvasToBlob(canvas, 'image/jpeg', jpegQuality);
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error(`Page ${pageNumber} could not be encoded.`));
        reader.readAsDataURL(blob);
      });

      if (!exportDoc) {
        exportDoc = new jsPDF({
          orientation: viewport.width > viewport.height ? 'l' : 'p',
          unit: 'pt',
          format: [viewport.width, viewport.height],
          compress: true,
          encryption: encryption
            ? {
                userPassword: encryption.userPassword,
                ownerPassword: encryption.ownerPassword ?? crypto.randomUUID(),
                userPermissions: encryption.permissions ?? [],
              }
            : undefined,
        });
      } else {
        exportDoc.addPage(
          [viewport.width, viewport.height],
          viewport.width > viewport.height ? 'landscape' : 'portrait',
        );
        exportDoc.setPage(recoveredPages.length + 1);
      }

      exportDoc.addImage(dataUrl, 'JPEG', 0, 0, viewport.width, viewport.height, undefined, 'FAST');
      recoveredPages.push(pageNumber);
      canvas.width = 0;
      canvas.height = 0;
    } catch {
      droppedPages.push(pageNumber);
    } finally {
      page?.cleanup?.();
    }
  }

  if (!exportDoc || !recoveredPages.length) {
    throw new Error('This PDF could not be rebuilt from readable pages in the browser.');
  }

  onProgress?.({
    current: recoveredPages.length,
    total: recoveredPages.length,
    label: 'Packing rebuilt PDF…',
  });

  return {
    blob: exportDoc.output('blob'),
    recoveredPages,
    droppedPages,
  } satisfies RenderedPdfRebuildResult;
}

export function findNonWhiteCanvasBounds(
  canvas: HTMLCanvasElement,
  threshold = 245,
  alphaThreshold = 12,
) {
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('The browser could not inspect this PDF page.');
  }

  const { width, height } = canvas;
  const data = context.getImageData(0, 0, width, height).data;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const red = data[index];
      const green = data[index + 1];
      const blue = data[index + 2];
      const alpha = data[index + 3];

      if (alpha > alphaThreshold && (red < threshold || green < threshold || blue < threshold)) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (maxX < minX || maxY < minY) {
    return null;
  }

  return {
    left: minX,
    top: minY,
    right: maxX + 1,
    bottom: maxY + 1,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  };
}

export function canvasBoundsToPdfCropRect(
  bounds: { left: number; top: number; right: number; bottom: number },
  renderScale: number,
  pageHeightAtScaleOne: number,
) {
  const x = bounds.left / renderScale;
  const width = Math.max(1, (bounds.right - bounds.left) / renderScale);
  const height = Math.max(1, (bounds.bottom - bounds.top) / renderScale);
  const y = Math.max(0, pageHeightAtScaleOne - bounds.bottom / renderScale);

  return {
    x,
    y,
    width,
    height,
  } satisfies PdfCropRect;
}
